import * as React from 'react'
import { NavigateFunction } from 'react-router-dom'

interface UseNavigationHandlingProps {
  isDownloadingRef: React.MutableRefObject<boolean>
  navigate: NavigateFunction
  confirmExitMessage: string
  customBackNavigation?: string
}

export const useNavigationHandling = ({
  isDownloadingRef,
  navigate,
  confirmExitMessage,
  customBackNavigation,
}: UseNavigationHandlingProps) => {
  const isHandlingPopRef = React.useRef(false)
  const isMountedRef = React.useRef(true)
  const bypassAlertConfirmationRef = React.useRef(false)

  const bypassAlertNavigationConfirmation = React.useCallback(() => {
    bypassAlertConfirmationRef.current = true
  }, [])

  React.useEffect(() => {
    isMountedRef.current = true

    const handlerExitPage = (e: BeforeUnloadEvent) => {
      if (isDownloadingRef.current) return
      if (bypassAlertConfirmationRef.current) {
        bypassAlertConfirmationRef.current = false
        return
      }
      e.preventDefault()
      e.returnValue = ''
      return ''
    }

    const handlePopState = (_e: PopStateEvent) => {
      if (isDownloadingRef.current) return
      if (bypassAlertConfirmationRef.current) {
        bypassAlertConfirmationRef.current = false
        window.removeEventListener('beforeunload', handlerExitPage)
        window.removeEventListener('popstate', handlePopState)
        if (customBackNavigation) {
          navigate(customBackNavigation)
        } else {
          navigate(-1)
        }
        return
      }
      if (isHandlingPopRef.current) {
        isHandlingPopRef.current = false
        return
      }
      const confirmExit = window.confirm(confirmExitMessage)
      if (confirmExit) {
        window.removeEventListener('beforeunload', handlerExitPage)
        window.removeEventListener('popstate', handlePopState)
        if (customBackNavigation) {
          navigate(customBackNavigation)
        } else {
          navigate(-1)
        }
      } else {
        isHandlingPopRef.current = true
        navigate(1)
      }
    }

    if (isMountedRef.current) {
      window.history.pushState(null, '', window.location.href)
    }

    window.addEventListener('popstate', handlePopState)
    window.addEventListener('beforeunload', handlerExitPage)

    return () => {
      isMountedRef.current = false
      window.removeEventListener('beforeunload', handlerExitPage)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [isDownloadingRef, navigate, confirmExitMessage, customBackNavigation])

  return { bypassAlertNavigationConfirmation }
}
